import { FilesetResolver, HandLandmarker } from '@mediapipe/tasks-vision'
import { config } from '../config'
import { useHandStore } from '../store/handStore'

type StopFn = () => void

type Landmark = { x: number; y: number; z: number }

function dist2D(a: Landmark, b: Landmark) {
  const dx = a.x - b.x
  const dy = a.y - b.y
  return Math.hypot(dx, dy)
}
function dist3D(a: Landmark, b: Landmark) {
  const dx = a.x - b.x
  const dy = a.y - b.y
  const dz = a.z - b.z
  return Math.hypot(dx, dy, dz)
}

export async function startHandTracker(
  video: HTMLVideoElement,
  getCubePos: () => [number, number, number] | null,
): Promise<StopFn> {
  let stopped = false

  const vision = await FilesetResolver.forVisionTasks(config.tracking.visionWasmBase)

  const handLandmarker = await HandLandmarker.createFromOptions(vision, {
    baseOptions: {
      modelAssetPath: config.tracking.handModel,
      delegate: 'GPU',
    },
    runningMode: 'VIDEO',
    numHands: 1,
    minHandDetectionConfidence: 0.5,
    minHandPresenceConfidence: 0.5,
    minTrackingConfidence: 0.5,
  })

  useHandStore.getState().setActive(true)

  let lastTs = -1
  let lastWallMs = 0
  let invisibleFrames = 0

  const tick = (now: number) => {
    if (stopped) return
    if (now - lastWallMs < config.tracking.handMinIntervalMs) {
      schedule()
      return
    }
    lastWallMs = now

    if (video.readyState < 2 || video.videoWidth === 0) {
      schedule()
      return
    }
    const ts = Math.max(performance.now(), lastTs + 1)
    lastTs = ts
    try {
      const result = handLandmarker.detectForVideo(video, ts)
      const handLm = result?.landmarks?.[0]
      const worldLm = result?.worldLandmarks?.[0]
      if (handLm && handLm.length >= 21) {
        invisibleFrames = 0
        // 位置：用 landmark[9]（middle finger MCP）作为掌心代理
        const palm = handLm[9]
        // 深度代理：用归一化 palm 宽度（5↔17 在 image 坐标内的距离）
        const palmWidth = dist2D(handLm[5], handLm[17])
        // 把 palmWidth 映射成 [-1, +1]：0.06=far, 0.20=near
        const depthProxy = -clamp((palmWidth - 0.13) / 0.07, -1, 1)
        // 通过 store mapping 时，handZ 会经过 (zCenter - handZ) * zScale → 我们直接给一个等价值
        // 让最终 cube z ≈ -depthProxy * 0.6（near → +0.6）
        // 反推 handZ：zCenter - handZ = -depthProxy  →  handZ = zCenter + depthProxy
        // 因为 zCenter=0，handZ = depthProxy；store zScale 已经设为合适值
        const handZForStore = depthProxy

        // Pinch 距离用 worldLandmarks（米），更稳定
        let pinchDist = 1
        if (worldLm && worldLm.length >= 21) {
          pinchDist = dist3D(worldLm[4], worldLm[8])
        } else {
          pinchDist = dist2D(handLm[4], handLm[8])
        }

        useHandStore
          .getState()
          .ingest(palm.x, palm.y, handZForStore, pinchDist, getCubePos())
      } else {
        invisibleFrames++
        if (invisibleFrames > 30) {
          useHandStore.getState().setHandVisible(false)
          useHandStore.getState().release()
        }
      }
    } catch {
      /* skip frame */
    }
    schedule()
  }

  const rvfc =
    'requestVideoFrameCallback' in HTMLVideoElement.prototype
      ? (video as any).requestVideoFrameCallback.bind(video)
      : null

  function schedule() {
    if (stopped) return
    if (rvfc) {
      rvfc(() => tick(performance.now()))
    } else {
      requestAnimationFrame(() => tick(performance.now()))
    }
  }

  schedule()

  return () => {
    stopped = true
    useHandStore.getState().setActive(false)
    useHandStore.getState().setHandVisible(false)
    try {
      handLandmarker.close()
    } catch {
      /* ignore */
    }
  }
}

function clamp(v: number, lo: number, hi: number) {
  return v < lo ? lo : v > hi ? hi : v
}
